/**
 * @file src/pages/device-settings/components/ProductionCertificatesTab.jsx
 * @description Third tab of Device Settings — list of devices with their
 * production certificate status and a "Generate" button per device.
 *
 * STATE OF THIS FEATURE:
 *  The actual production-CSR generation handler is intentionally a no-op
 *  right now. The original code had it commented out — preserved here as
 *  a TODO so the visual UI works while the wiring is decided later.
 *
 *  When ready to enable, uncomment the body of handleGenerateProduction
 *  below — it already calls the existing useGenerateProductionCSR hook
 *  via the parent.
 *
 * Production certs require the device's compliance cert to be active first.
 * The Generate button is disabled until that's true.
 */

import React, { useState } from "react";
// import { useQueryClient } from "@tanstack/react-query";
// import toast from "react-hot-toast";
// import { useGenerateProductionCSR } from "../useGenerateProductionCSR";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import StatusBadge from "./StatusBadge";

const ProductionCertificatesTab = ({ devices }) => {
  // const queryClient = useQueryClient();
  // const { mutate: generateProductionCSR } = useGenerateProductionCSR();
  const [isGeneratingCert, setIsGeneratingCert] = useState(false);

  // ── Handler ─────────────────────────────────────────────────────────────
  // TODO: enable when ready. Original implementation kept inline so it's
  // a one-line change to wire it back up.
  const handleGenerateProduction = (/* deviceId */) => {
    setIsGeneratingCert(true);
    // generateProductionCSR(deviceId, {
    //   onSuccess: () => {
    //     queryClient.invalidateQueries(["devices"]);
    //     setIsGeneratingCert(false);
    //   },
    //   onError: (error) => {
    //     const isAuthError = error?.response?.message?.includes("logged in");
    //     if (isAuthError) {
    //       toast.error("you must be logged in to access this resource", {
    //         id: "auth-error",
    //       });
    //     }
    //     setIsGeneratingCert(false);
    //     toast.error(error.message);
    //   },
    // });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium text-foreground">
          Production Certificates
        </h2>
        <span className="text-sm text-muted-foreground">
          Manage production certificates
        </span>
      </div>

      <div className="space-y-4">
        {devices.map((device) => {
          const complianceActive =
            device.certificates.compliance.status === "active";

          return (
            <div
              key={device.deviceId}
              className="bg-muted/30 border border-border rounded-xl p-6"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Icon name="Award" size={16} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">
                      Device {device.deviceSerialNumber}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {device.organizationName}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <StatusBadge status={device.certificates.production.status} />
                  <Button
                    onClick={() => handleGenerateProduction(device.deviceId)}
                    disabled={isGeneratingCert || !complianceActive}
                    className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                  >
                    {isGeneratingCert ? (
                      <Icon name="Loader" size={16} className="animate-spin" />
                    ) : (
                      <Icon name="Generate" size={16} />
                    )}
                    <span className="ml-2">
                      {device.certificates.production.status === "active"
                        ? "Generated"
                        : "Generate"}
                    </span>
                  </Button>
                </div>
              </div>

              {!complianceActive && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <Icon
                      name="AlertTriangle"
                      size={14}
                      className="inline mr-1"
                    />
                    Compliance certificate required before generating production
                    certificate
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProductionCertificatesTab;
